<i18n>
{
	"no": {
		"applyFilter": "Bruk filter",
		"clearFilter": "Nullstill",
		"contains": "Inneholder",
		"endsWith": "Slutter med",
		"equals": "Er lik",
		"greaterThan": "Større enn",
		"greaterThanOrEqual": "Større enn eller lik",
		"inRangeInclusive": "Mellom - inklusive",
		"inRangeExclusive": "Mellom - eksklusiv",
		"lessThan": "Mindre enn",
		"lessThanOrEqual": "Mindre enn eller lik",
		"notContains": "Inneholder ikke",
		"notEqual": "Ikke lik",
		"startsWith": "Begynner med",
		"smart": "Smart"
	},
	"en": {
		"applyFilter": "Apply Filter",
		"clearFilter": "Clear Filter",
		"contains": "Contains",
		"endsWith": "Ends with",
		"equals": "Equals",
		"greaterThan": "Greater Than",
		"greaterThanOrEqual": "Greater than or equals",
		"inRangeInclusive": "In range - inclusive",
		"inRangeExclusive": "In range - exclusive",
		"lessThan": "Less Than",
		"lessThanOrEqual": "Less than or equals",
		"notContains": "Not contains",
		"notEqual": "Not equal",
		"startsWith": "Starts with",
		"smart": "Smart"
	}
}
</i18n>
<template>
	<t-popover
		v-if="filterable"
		:showPopover="popoverIsVisible && filterActive"
		:marginY="4"
		position="bottom"
		relative
		:width="300"
		@close="disableActiveFilter"
	>
		<t-icon-button
			v-if="columnDefinition.headerFilterButtonOnly"
			slot="target"
			type="bp"
			icon="filter-list"
			size="small"
			tabindex="0"
			@click="popoverSetVisible()"
		/>
		<t-text-input-button
			v-else-if="columnDefinition.headerFilterInputWithButton && filterActive"
			ref="buttonInput"
			slot="target"
			v-model="firstValue"
			:class="{'grid-header-filter-active': firstValue }"
			type="bp"
			icon="more"
			size="small"
			tabindex="0"
			disableButtonTabbing
			@input="onFloatingFilterChanged"
			@button:click="onButtonClick"
			@blur="onInputTextBlur"
			@button:blur="onInputButtonBlur"
			@keydown.passive="onInputTabAway"
			@button:keydown="onButtonTabAway"
		/>
		<t-text-input
			v-else
			slot="target"
			v-model="firstValue"
			:class="{'grid-header-filter-active': firstValue}"
			size="small"
			tabindex="0"
			@input="onFloatingFilterChanged"
			@focus="onTextFocus"
			@click="onTextFocus"
		/>
		<div class="filterPopover" style="padding: 10px;">
			<component
				:is="customFilterComponent"
				v-if="customFilterComponent"
				ref="customFilterComponent"
				:value="firstValue"
				:customFilterStorage="customFilterStorage"
				:columnDefinition="columnDefinition"
				@filter-added="onCustomFilterAdded"
			/>
			<template v-else>
				<t-select
					:items="operations"
					:selectedItem="selectedOperation"
					displayName="label"
					style="margin-bottom: 10px;"
					relative
					@change="onOperationChanged"
				>
					<template v-slot:displayedValue>
						{{$t(selectedOperation.label)}}
					</template>
					<template #optionItemValue="{ item }">
						{{$t(item.label)}}
					</template>
				</t-select>
				<t-date-picker
					v-if="columnDefinition.type == 'date'"
					v-model="firstValue"
					:required="true"
					:dateFormat="columnDefinition.dateFormat"
					style="margin-bottom: 10px;"
					relative
				/>
				<t-text-input
					v-else
					v-model="firstValue"
					:required="true"
					style="margin-bottom: 10px;"
				/>

				<t-date-picker
					v-if="needsTwoValues && columnDefinition.type == 'date'"
					v-model="secondValue"
					:required="needsTwoValues"
					:dateFormat="columnDefinition.dateFormat"
					style="margin-bottom: 10px;"
					relative
				/>
				<t-text-input
					v-else-if="needsTwoValues"
					v-model="secondValue"
					:required="needsTwoValues"
					style="margin-bottom: 10px;"
				/>
			</template>
			<span style="display:flex;padding-top:4px;">
				<t-button appearance="primary" @click="onAddFilter(true)">{{$t('applyFilter')}}</t-button>
				<t-button appearance="minimal" @click="onCancel">{{$t('clearFilter')}}</t-button>
			</span>
		</div>
	</t-popover>
</template>

<script>
import * as dateFilter from './filterFactory/date';
import * as numberFilter from './filterFactory/number';
import * as stringFilter from './filterFactory/string';
import * as addressFilter from './filterFactory/address';
import * as arrayFilter from './filterFactory/array';
import TDialog from '../../overlays/TDialog';
import TPopover from '../../overlays/TPopover';
import TButton from '../../buttons/TButton';
import TPane from '../../layers/TPane';
import popoverMixin from './mixins/popover.js';
import moment from 'moment';

export default {
	name: 'TFilter',
	components: {
		TDialog,
		TPane,
		TPopover,
		TButton,
	},
	mixins: [popoverMixin],
	props: { columnDefinition: Object, },
	data() {
		return {
			selectedOperationId: null,
			firstValue: '',
			secondValue: '',
			customFilterStorage: {},
			filterTimeout: null,
			disableFilterBlur: false,
			filterActive: false,
			blurring: false
		};
	},
	computed: {
		selectedOperation() {
			return this.operations.find(op => op.id === this.selectedOperationId);
		},
		needsTwoValues() {
			return this.selectedOperation.requiredInputCount > 1;
		},
		filterable() {
			return this.columnDefinition.filterable !== false;
		},
		operations() {
			if (this.columnDefinition.customFilter && this.columnDefinition.customFilter.operations) {
				return this.columnDefinition.customFilter.operations;
			} else if (this.columnDefinition.type === 'number') {
				return numberFilter.operations;
			} else if (this.columnDefinition.type === 'date') {
				return dateFilter.operations;
			} else if (this.columnDefinition.type === 'address') {
				return addressFilter.operations;
			} else if (this.columnDefinition.type === 'array') {
				return arrayFilter.operations;
			} else {
				return stringFilter.operations;
			}
		},
		filterFactory() {
			if (this.columnDefinition.customFilter && this.columnDefinition.customFilter.factoryFunc) {
				return this.columnDefinition.customFilter.factoryFunc;
			} else if (this.columnDefinition.type === 'number') {
				return numberFilter.filterFactory;
			} else if (this.columnDefinition.type === 'date') {
				return dateFilter.filterFactory;
			} else if (this.columnDefinition.type === 'address') {
				return addressFilter.filterFactory;
			} else if (this.columnDefinition.type === 'array') {
				return arrayFilter.filterFactory;
			} else {
				return stringFilter.filterFactory;
			}
		},
		customFilterComponent() {
			return this.columnDefinition.customFilterComponent;
		}
	},
	beforeMount() {
		this.selectedOperationId = this.operations[0].id;
	},
	methods: {
		onOperationChanged(e) {
			let op;
			if (e.target)
				op = e.target.value;
			else
				op = e;
			this.selectedOperationId = op.id;
		},
		formatInput(value) {
			if (this.columnDefinition.customFilter && this.columnDefinition.customFilter.inputFormatter) {
				return this.columnDefinition.customFilter.inputFormatter(value);
			} else if (this.columnDefinition.type === 'number') {
				return isNaN(value) ? value : Number.parseFloat(value);
			} else if (this.columnDefinition.type === 'date') {
				let date = moment(value, this.columnDefinition.dateFormat);
				if (date.isValid()) {
					return date.format(this.columnDefinition.dateFormat);
				} else {
					return value;
				}
			} else {
				return value;
			}
		},
		onAddFilter(clicked = false) {
			let filter;
			if (this.customFilterComponent) {
				filter = this.$refs.customFilterComponent.getFilterFunc();
			} else {
				if (this.firstValue.length === 0) {
					this.$emit('filter-removed', this.columnDefinition.field);
					if (clicked) {
						this.disableActiveFilter();
					}
					return;
				}
				this.firstValue = this.formatInput(this.firstValue);
				this.secondValue = this.formatInput(this.secondValue);
				filter = this.filterFactory(this.columnDefinition, this.selectedOperation.id, this.firstValue, this.secondValue);
			}
			filter.columnName = this.columnDefinition.field;
			this.$emit('filter-created', filter);
			if (clicked) {
				this.disableActiveFilter();
			}
		},
		onCancel() {
			this.popoverSetHidden();
			this.selectedOperationId = this.operations[0].id;
			this.firstValue = '';
			this.secondValue = '';
			this.customFilterStorage = {};
			this.$emit('filter-removed', this.columnDefinition.field);
			this.disableActiveFilter();
		},
		onFloatingFilterChanged() {
			clearTimeout(this.filterTimeout);

			this.filterTimeout = setTimeout(() => {
				this.onAddFilter();
			},800);
		},
		onCustomFilterAdded(filterFunc) {
			filterFunc.columnName = this.columnDefinition.field;
			this.$emit('filter-created', filterFunc);
			this.disableActiveFilter();
		},
		async onTextFocus() {
			if (this.columnDefinition.headerFilterInputWithButton) {
				this.filterActive = true;
				await this.$nextTick();
				this.$refs.buttonInput.focus();
			} else {
				this.popoverSetVisible();
			}
		},
		onInputTabAway(e) {
			if ((e.key === 'Tab' || e.key === 'Enter') && e.shiftKey) {
				this.disableActiveFilter();
			}
		},
		onButtonTabAway(e) {
			if ((e.key === 'Tab' || e.key === 'Enter') && !e.shiftKey) {
				this.disableActiveFilter();
			}
		},
		onInputTextBlur(e) {
			if (this.popoverIsVisible) return;
			if (!e.relatedTarget || !e.relatedTarget.classList.contains('textInputButton')) this.disableActiveFilter();
		},
		onInputButtonBlur(e) {
			if (this.popoverIsVisible) return;
			if (!e.relatedTarget || !e.relatedTarget.classList.contains('text-input-with-button')) this.disableActiveFilter();
		},
		onButtonClick() {
			if (this.popoverIsVisible) {
				this.popoverSetHidden();
			} else {
				this.popoverSetVisible();
			}
		},
		disableActiveFilter() {
			this.disableFilterBlur = false;
			this.filterActive = false;
			this.popoverSetHidden();
		},
	}
};
</script>
